import React from "react";
import { Column } from "@/components/Datatable/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PencilSquareIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "@/utils/formatCurrency";
import { EventItem } from "../../slices/eventListSlice";
import EventActionMenu from "../EventActionMenu/EventActionMenu";

interface CreateEventColumnsProps {
  onDuplicateEvent: (eventId: number) => void;
  onEditEvent: (uuid: string) => void;
  onDeleteEvent: (id: number) => void;
  onViewSessions: (uuid: string) => void;
}

export function createEventColumns({
  onDuplicateEvent,
  onEditEvent,
  onDeleteEvent,
  onViewSessions,
}: CreateEventColumnsProps): Column<EventItem>[] {
  return [
    {
      header: "Event Name",
      accessorKey: "eventName",
      enableSorting: true,
    },
    {
      header: "Event Code",
      accessorKey: "eventCode",
      enableSorting: true,
    },
    {
      header: "Event Type",
      accessorKey: "eventTypeName",
      cell: ({ row }) => row.original.eventTypeName || "--",
    },
    {
      header: "Category",
      accessorKey: "eventCategoryName",
      cell: ({ row }) => row.original.eventCategoryName || "--",
    },
    {
      header: "Price",
      accessorKey: "eventPrice",
      enableSorting: true,
      cell: ({ row }) => formatCurrency(row.original.eventPrice),
    },
    {
      header: "Status",
      accessorKey: "isPublished",
      enableSorting: true,
      cell: ({ row }) => {
        const isPublished = row.original.isPublished;

        return (
          <Badge variant={isPublished ? "default" : "secondary"}>
            {isPublished ? "Published" : "Draft"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const stopPropagation = (evt: React.MouseEvent) => {
          evt.stopPropagation();
        };

        return (
          <div className="flex items-center justify-end gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      stopPropagation(e);
                      onEditEvent(row.original.uuid);
                    }}
                    variant="ghost"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-gray-600" />
                    <span className="sr-only">Edit Event</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit Event</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="h-8 w-8 p-0 relative"
                    onClick={(e) => {
                      stopPropagation(e);
                      onViewSessions(row.original.uuid);
                    }}
                    variant="ghost"
                  >
                    <CalendarDaysIcon className="h-4 w-4 text-gray-600" />
                    {row.original.sessionCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                        {row.original.sessionCount > 99 ? "99+" : row.original.sessionCount}
                      </span>
                    )}
                    <span className="sr-only">Sessions ({row.original.sessionCount})</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Sessions ({row.original.sessionCount})</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <EventActionMenu
              eventId={row.original.id}
              eventUuid={row.original.uuid}
              onDeleteEvent={onDeleteEvent}
              onDuplicateEvent={onDuplicateEvent}
            />
          </div>
        );
      },
    },
  ];
}
